import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Car, Receipt, Clock,
  ArrowRight, CheckCircle, AlertCircle, Wrench,
  Bell, X, Trash2, Calendar, Hash, ThumbsUp, ThumbsDown, Phone,
  RefreshCw
} from 'lucide-react';
import {
 BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import TopBar from '../components/layout/TopBar';
import { useNotifications } from '../context/NotificationContext';
import { useAdminAuth }       from '../context/Adminauthcontext';
import { getAllBookings }      from '../api';
import './DashboardPage.css';

const PIE_COLORS = ['#007BFF','#0056CC','#90C4FF','#6B7280'];

const STATUS_META = {
  'completed':            { label:'Completed',   cls:'badge-success' },
  'in-progress':          { label:'In Progress', cls:'badge-warning' },
  'waiting':              { label:'Waiting',     cls:'badge-info'    },
  'pending':              { label:'Pending',     cls:'badge-neutral' },
  'pending_confirmation': { label:'Pending',     cls:'badge-neutral' },
  'confirmed':            { label:'Confirmed',   cls:'badge-info'    },
  'cancelled':            { label:'Cancelled',   cls:'badge-neutral' },
};

export default function DashboardPage() {
  const { admin } = useAdminAuth();
  const { notifications, dismiss, clearAll, confirmBooking, rejectBooking, refresh: refreshNotifs } = useNotifications();

  const firstName = admin?.name?.split(' ')[0] || 'Admin';
  const hour      = new Date().getHours();
  const greeting  = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  /* ── Fetch all bookings from API ── */
  const [allBookings, setAllBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(true);

  const loadBookings = useCallback(async () => {
    try {
      const data = await getAllBookings();
      setAllBookings(data);
    } catch (e) {
      console.error('Failed to load bookings:', e.message);
    } finally {
      setLoadingBookings(false);
    }
  }, []);

  useEffect(() => { loadBookings(); }, [loadBookings]);

  /* ── Poll bookings every 10s ── */
  useEffect(() => {
    const iv = setInterval(loadBookings, 10000);
    return () => clearInterval(iv);
  }, [loadBookings]);

  /* ── KPIs from real bookings ── */
  const totalCount     = allBookings.length;
  const pendingCount   = allBookings.filter(b => b.status === 'pending_confirmation').length;
  const activeCount    = allBookings.filter(b => b.status === 'in-progress' || b.status === 'waiting' || b.status === 'confirmed').length;
  const completedCount = allBookings.filter(b => b.status === 'completed').length;

  const KPIS = [
    { label:'Total Bookings',   value:String(totalCount),     change:'all time',    up:null,  icon:<Car size={20}/>,     sub:'all customers'        },
    { label:'Active Jobs',      value:String(activeCount),    change:'in progress', up:true,  icon:<Wrench size={20}/>,  sub:'vehicles in service'  },
    { label:'Awaiting Confirm', value:String(pendingCount),   change:'need action', up:null,  icon:<Clock size={20}/>,   sub:'pending your review'  },
    { label:'Completed',        value:String(completedCount), change:'all time',    up:true,  icon:<Receipt size={20}/>, sub:'services done'         },
  ];

  /* ── Recent bookings — latest 5 ── */
  const recentBookings = allBookings.slice(0, 5).map(b => ({
    id:       b.id,
    customer: b.ownerName || 'Customer',
    vehicle:  b.vehicleMake ? `${b.vehicleMake} ${b.vehicleModel || ''}`.trim() : '—',
    service:  Array.isArray(b.services) ? b.services[0] : '—',
    status:   b.status || 'pending',
    time:     b.timeSlot || '—',
  }));

  /* ── Service mix from bookings ── */
  const serviceCounts = allBookings.reduce((acc, b) => {
    const cat = b.serviceCategory || (
      (b.services||[]).some(s => /wash|detail|ceramic/i.test(s)) ? 'Wash/Detail' :
      (b.services||[]).some(s => /elec|ecu|ac/i.test(s))        ? 'Electrical'  :
      (b.services||[]).some(s => /tyre|wheel|align/i.test(s))   ? 'Tyres'       : 'Mechanical'
    );
    acc[cat] = (acc[cat] || 0) + 1;
    return acc;
  }, {});
  const serviceMix = Object.entries(serviceCounts).length > 0
    ? Object.entries(serviceCounts).map(([name, value]) => ({ name, value }))
    : [{ name:'Mechanical', value:42 },{ name:'Wash/Detail', value:28 },{ name:'Electrical', value:16 },{ name:'Tyres', value:14 }]
      .map(({name,value}) => ({ name, value }));

  /* ── Jobs-by-day from bookings ── */
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const jobsByDay = days.map((day, i) => ({
    day,
    jobs: allBookings.filter(b => {
      if (!b.date) return false;
      const d = new Date(b.date);
      return !isNaN(d) && d.getDay() === (i + 1) % 7;
    }).length || 0,
  }));

  const bookingNotifs = notifications.filter(n => n.type === 'booking');

  return (
    <>
      <TopBar
        title="Dashboard"
        subtitle={`${greeting}, ${firstName} — ${new Date().toDateString()}`}
      />
      <div className="admin-content">

        {/* ── KPI Cards ── */}
        <div className="kpi-grid stagger">
          {KPIS.map(({ label, value, change, up, icon, sub }) => (
            <div key={label} className="kpi-card card afu">
              <div className="kpi-card__top">
                <div className="kpi-card__icon">{icon}</div>
                <span className={`kpi-card__change ${up === true ? 'up' : up === false ? 'down' : 'neutral'}`}>
                  {up === true && <TrendingUp size={12}/>}
                  {up === false && <TrendingDown size={12}/>}
                  {change}
                </span>
              </div>
              <p className="kpi-card__value">
                {loadingBookings ? <span className="kpi-skeleton"/> : value}
              </p>
              <p className="kpi-card__label">{label}</p>
              <p className="kpi-card__sub">{sub}</p>
            </div>
          ))}
        </div>

        {/* ── Charts ── */}
        <div className="charts-row">
          {/* Service mix pie */}
          <div className="card chart-card">
            <div className="chart-card__header">
              <div>
                <h3 className="chart-card__title">Bookings by Category</h3>
                <p className="chart-card__sub">Breakdown of {totalCount} total bookings</p>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={loadBookings} title="Refresh">
                <RefreshCw size={12}/>
              </button>
            </div>
            {totalCount === 0 ? (
              <div style={{ height:180, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)', fontSize:13 }}>
                No bookings yet
              </div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={serviceMix} cx="50%" cy="50%" innerRadius={40} outerRadius={65}
                      dataKey="value" paddingAngle={3}>
                      {serviceMix.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]}/>)}
                    </Pie>
                    <Tooltip contentStyle={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:8, fontSize:12 }}
                      formatter={v => [`${v} bookings`, '']}/>
                  </PieChart>
                </ResponsiveContainer>
                <div className="pie-legend">
                  {serviceMix.map(({ name, value }, i) => (
                    <div key={name} className="pie-legend__item">
                      <span className="pie-legend__dot" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }}/>
                      <span>{name}</span>
                      <span className="pie-legend__val">{value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Jobs by day */}
          <div className="card chart-card chart-card--sm">
            <div className="chart-card__header">
              <div>
                <h3 className="chart-card__title">Jobs by Day of Week</h3>
                <p className="chart-card__sub">All-time booking distribution</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={jobsByDay} barSize={22} margin={{ top:4, right:4, left:0, bottom:0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                <XAxis dataKey="day" tick={{ fontSize:11, fill:'var(--text-muted)' }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize:10, fill:'var(--text-muted)' }} axisLine={false} tickLine={false}/>
                <Tooltip contentStyle={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:8, fontSize:12 }}/>
                <Bar dataKey="jobs" fill="var(--accent)" radius={[4,4,0,0]} name="Bookings"/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── Bottom row ── */}
        <div className="bottom-row">
          {/* Recent Bookings */}
          <div className="card">
            <div className="chart-card__header" style={{ marginBottom:16 }}>
              <h3 className="chart-card__title">Recent Bookings</h3>
              <Link to="/appointments" className="btn btn-ghost btn-sm">View All <ArrowRight size={12}/></Link>
            </div>
            <div className="recent-list">
              {loadingBookings ? (
                <p style={{ fontSize:12, color:'var(--text-muted)', textAlign:'center', padding:'20px 0' }}>
                  Loading…
                </p>
              ) : recentBookings.length === 0 ? (
                <p style={{ fontSize:12, color:'var(--text-muted)', textAlign:'center', padding:'20px 0' }}>
                  No bookings yet. They appear here when customers book.
                </p>
              ) : recentBookings.map(b => {
                const meta = STATUS_META[b.status] || { label: b.status, cls: 'badge-neutral' };
                return (
                  <div key={b.id} className="recent-item">
                    <div className="recent-item__avatar">
                      {b.customer.split(' ').map(w => w[0]).join('').slice(0,2)}
                    </div>
                    <div className="recent-item__info">
                      <p className="recent-item__name">{b.customer}</p>
                      <p className="recent-item__detail">{b.vehicle} · {b.service}</p>
                    </div>
                    <div className="recent-item__right">
                      <span className={`badge ${meta.cls}`}>{meta.label}</span>
                      <span className="recent-item__time">{b.time}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick alerts */}
          <div className="card">
            <div className="chart-card__header" style={{ marginBottom:16 }}>
              <h3 className="chart-card__title">Quick Overview</h3>
            </div>
            <div className="dash-alerts">
              {pendingCount > 0 && (
                <div className="dash-alert dash-alert--warning">
                  <Clock size={16}/>
                  <span><strong>{pendingCount}</strong> booking{pendingCount > 1 ? 's' : ''} awaiting confirmation</span>
                </div>
              )}
              {activeCount > 0 && (
                <div className="dash-alert dash-alert--success">
                  <Wrench size={16}/>
                  <span><strong>{activeCount}</strong> vehicle{activeCount > 1 ? 's' : ''} currently in service</span>
                </div>
              )}
              {completedCount > 0 && (
                <div className="dash-alert dash-alert--success">
                  <CheckCircle size={16}/>
                  <span><strong>{completedCount}</strong> service{completedCount > 1 ? 's' : ''} completed</span>
                </div>
              )}
              {totalCount === 0 && (
                <div className="dash-alert dash-alert--warning">
                  <AlertCircle size={16}/>
                  <span>No bookings yet. Share your booking link with customers.</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Booking Notification Box ── */}
        <div className="dash-notif-box">
          <div className="dash-notif-box__header">
            <div className="dash-notif-box__title">
              <Bell size={16}/>
              <span>Booking Notifications</span>
              {bookingNotifs.filter(n => !n.read).length > 0 && (
                <span className="dash-notif-box__badge">
                  {bookingNotifs.filter(n => !n.read).length} new
                </span>
              )}
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button className="btn btn-ghost btn-sm" onClick={refreshNotifs} title="Refresh">
                <RefreshCw size={13}/>
              </button>
              {bookingNotifs.length > 0 && (
                <button className="dash-notif-box__clear" onClick={clearAll}>
                  <Trash2 size={13}/> Clear all
                </button>
              )}
            </div>
          </div>

          {bookingNotifs.length === 0 ? (
            <div className="dash-notif-box__empty">
              <CheckCircle size={28}/>
              <p>No booking notifications</p>
            </div>
          ) : (
            <div className="dash-notif-box__list">
              {bookingNotifs.map(n => (
                <div key={n.id}
                  className={[
                    'dash-notif-item',
                    !n.read                    ? 'dash-notif-item--unread'    : '',
                    n.adminStatus==='confirmed' ? 'dash-notif-item--confirmed' : '',
                    n.adminStatus==='rejected'  ? 'dash-notif-item--rejected'  : '',
                  ].filter(Boolean).join(' ')}>

                  <div className="dash-notif-item__content">
                    <div className="dash-notif-item__top">
                      <span className="dash-notif-item__customer">
                        {n.booking?.customer || n.message}
                      </span>
                      <span className="dash-notif-item__time">{n.time || n.createdAt}</span>
                    </div>

                    {n.booking && (
                      <div className="dash-notif-item__details">
                        <div className="dash-notif-detail"><Car size={11}/><span>{n.booking.vehicle}</span></div>
                        <div className="dash-notif-detail"><Hash size={11}/><span>{n.booking.reg}</span></div>
                        <div className="dash-notif-detail"><Wrench size={11}/><span>{n.booking.service}</span></div>
                        <div className="dash-notif-detail"><Calendar size={11}/><span>{n.booking.date} · {n.booking.time}</span></div>
                        {n.booking.phone && (
                          <div className="dash-notif-detail"><Phone size={11}/><span>{n.booking.phone}</span></div>
                        )}
                      </div>
                    )}

                    {n.adminStatus === 'pending' && (
                      <div className="dash-notif-item__actions">
                        <button className="notif-confirm-btn" onClick={() => confirmBooking(n.id)}>
                          <ThumbsUp size={12}/> Confirm Booking
                        </button>
                        <button className="notif-reject-btn" onClick={() => rejectBooking(n.id)}>
                          <ThumbsDown size={12}/> Reject
                        </button>
                      </div>
                    )}
                    {n.adminStatus === 'CONFIRMED' || n.adminStatus === 'confirmed' ? (
                      <div className="dash-notif-item__status dash-notif-item__status--confirmed">
                        <CheckCircle size={12}/> Confirmed — customer notified
                      </div>
                    ) : null}
                    {n.adminStatus === 'REJECTED' || n.adminStatus === 'rejected' ? (
                      <div className="dash-notif-item__status dash-notif-item__status--rejected">
                        <AlertCircle size={12}/> Rejected — booking cancelled
                      </div>
                    ) : null}
                  </div>

                  <button className="dash-notif-item__dismiss" onClick={() => dismiss(n.id)}>
                    <X size={13}/>
                  </button>
                  {!n.read && (n.adminStatus === 'pending' || n.adminStatus === 'PENDING') && (
                    <div className="dash-notif-item__dot"/>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="dash-notif-box__footer">
            <Link to="/appointments" className="btn btn-ghost btn-sm">
              View All Appointments <ArrowRight size={13}/>
            </Link>
          </div>
        </div>

      </div>

    </>
  );
}